import axios from "axios";
import { ApiResourceBuilder } from "core/builder";
import { BaseService } from "core/service";
import { getBaseAPI } from "../../../util/get-base-api";

class GenericService {
  /*
     urlMap: {
       fetch: '',
     }
   */
  constructor(urlMap) {
    this.mUrlMap = urlMap;
  }

  getInstance() {
    return this;
  }

  async fetch({ filters, order, pagination, group, transforms }) {
    const resourceRoute = ApiResourceBuilder.buildFromParams(
      this.mUrlMap.fetch,
      { filters, order, pagination, group, transforms }
    );
    const result = await this.networkGet(resourceRoute);

    if (result.success) {
      result.total = result.headers.total;
      result.perPage = parseInt(result.headers["per-page"], 10);
    }

    return result;
  }

  async create(params) {
    return await this.networkPost(this.mUrlMap.create, params);
  }

  async update(uuid, params) {
    return await this.networkUpdate(`${this.mUrlMap.update}/${uuid}`, params);
  }

  async destroy(uuid) {
    return await this.networkDelete(`${this.mUrlMap.delete}/${uuid}`);
  }

  async stream({ order, filters, group }, callback) {
    if (!callback) {
      return true;
    }

    let totalPages = 1;

    for (let page = 1; page <= totalPages; page++) {
      const result = await this.fetch({
        filters,
        order,
        group,
        pagination: { page }
      });

      if (!result.success) {
        return false;
      }

      for (const registry of result.data) {
        await callback(registry);
      }

      totalPages = Math.ceil(parseFloat(result.total) / result.perPage);
    }

    return true;
  }

  async networkGet(url, config = {}) {
    let response, success;
    const wrappedConfig = this._wrapConfig(config);
    const cancelToken =
      BaseService.sCancelToken && BaseService.sCancelToken.token
        ? { cancelToken: BaseService.sCancelToken.token }
        : {};

    try {
      const baseURL = getBaseAPI()
      
      response = await axios.get(baseURL + url, {
        ...wrappedConfig,
        ...cancelToken
      });
      success = true;
    } catch (e) {
      if (e instanceof axios.Cancel) {
        e.response = {
          ...(e.response || {}),
          data: { error: { userCancelled: true } }
        };
      }
      response = e.response;
      success = false;
    }

    const fatal = !response || response.status >= 500;

    const data = fatal
      ? !response || (response.data && response.data.errors)
      : response.data;
    const headers = fatal ? null : response.headers;

    return {
      data,
      headers,
      success,
      userCancelled: (data.error || {}).userCancelled
    };
  }

  async networkPost(url, body, config = {}) {
    let response, success;

    try {
      const baseURL = getBaseAPI()
      response = await axios.post(
        baseURL + url,
        body,
        this._wrapConfig(config)
      );
      success = true;
    } catch (e) {
      response = e.response;
      success = false;
    }

    const fatal = !response || response.status >= 500;

    const data = fatal
      ? !response || (response.data && response.data.errors)
      : response.data;
    const headers = fatal ? null : response.headers;

    return { data, headers, success };
  }

  async networkUpdate(url, body, config = {}) {
    let response, success;

    try {
      const baseURL = getBaseAPI()
      response = await axios.patch(
        baseURL + url,
        body,
        this._wrapConfig(config)
      );
      success = true;
    } catch (e) {
      response = e.response;
      success = false;
    }

    const fatal = !response || response.status >= 500;

    const data = fatal
      ? !response || (response.data && response.data.errors)
      : response.data;
    const headers = fatal ? null : response.headers;

    return { data, headers, success };
  }

  async networkDelete(url, config = {}) {
    let response, success;

    try {
      const baseURL = getBaseAPI()
      response = await axios.delete(
        baseURL + url,
        this._wrapConfig(config)
      );
      success = true;
    } catch (e) {
      response = e.response;
      success = false;
    }

    const fatal = !response || response.status >= 500;

    const data = fatal
      ? !response || (response.data && response.data.errors)
      : response.data;
    const headers = fatal ? null : response.headers;

    return { data, headers, success };
  }

  _wrapConfig(config) {
    const headers = {};
    const cancelToken =
      BaseService.sCancelToken && BaseService.sCancelToken.token
        ? { cancelToken: BaseService.sCancelToken.token }
        : {};

    if (!!BaseService._config.authorizationToken) {
      headers[
        "Authorization"
      ] = `Bearer ${BaseService._config.authorizationToken}`;
    }

    return { ...config, ...cancelToken, headers };
  }
}

export default GenericService;



// WEBPACK FOOTER //
// ./src/core/data/service/generic.service.js